import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Boxes, ArrowDownCircle, ArrowUpCircle, Loader2, Search, FileSpreadsheet } from "lucide-react";
import { api, API, fmtDate } from "@/lib/apiClient";import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const Field = ({ label, children }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </div>
);

const Card = ({ label, value, warna }) => (
  <div className="glass rounded-2xl p-5">
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <div className={`mt-2 font-display text-2xl font-light ${warna}`}>{value}</div>
  </div>
);

export const StockBoard = ({ role }) => {
  const canDetail = ["kumendan", "manager", "staff"].includes(role);
  const [rows, setRows] = useState([]);
  const [moves, setMoves] = useState([]);
  const [cari, setCari] = useState("");
  const [katFilter, setKatFilter] = useState("ALL");
  const [sel, setSel] = useState("");
  const [jenis, setJenis] = useState("masuk");
  const [qty, setQty] = useState("");
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, mv] = await Promise.all([api.get("/materials"), api.get("/stock-moves")]);
      setRows(m.data);
      setMoves(mv.data);
    } catch {
      toast.error("Gagal memuat data stok");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = cari.trim().toLowerCase();
  const kategoriOptions = useMemo(
    () => [...new Set(rows.map((r) => r.kategori).filter(Boolean))].sort(),
    [rows]
  );
  const view = useMemo(
    () =>
      rows.filter(
        (r) =>
          (katFilter === "ALL" || r.kategori === katFilter) &&
          (!q ||
            [r.kategori, r.jenis, r.merk, r.ukuran].some((v) => (v || "").toLowerCase().includes(q)))
      ),
    [rows, q, katFilter]
  );

  const rekap = useMemo(() => {
    const agg = {};
    moves.forEach((m) => {
      const a = (agg[m.material_id] = agg[m.material_id] || {
        masuk: 0,
        keluar: 0,
        tanggal: "",
        by: "",
      });
      a[m.jenis] += Number(m.qty || 0);
      if (String(m.tanggal || "") >= a.tanggal) {
        a.tanggal = String(m.tanggal || "");
        a.by = m.by || "";
      }
    });
    return rows
      .filter((r) => katFilter === "ALL" || r.kategori === katFilter)
      .map((r) => ({
        ...r,
        masuk: agg[r.id]?.masuk || 0,
        keluar: agg[r.id]?.keluar || 0,
        last_tanggal: agg[r.id]?.tanggal || "",
        last_by: agg[r.id]?.by || "",
      }));
  }, [rows, moves, katFilter]);

  const totalMasuk = rekap.reduce((a, r) => a + r.masuk, 0);
  const totalKeluar = rekap.reduce((a, r) => a + r.keluar, 0);
  const habis = rows.filter((r) => Number(r.stok || 0) <= 0).length;

  const simpan = async () => {
    if (!sel) return toast.error("Pilih bahan dulu");
    setBusy(true);
    try {
      const { data } = await api.post("/stock-moves", {
        material_id: sel,
        jenis,
        qty: Number(qty) || 0,
        tanggal,
        catatan,
      });
      toast.success(
        `Stok ${jenis} ${Number(qty)} tercatat · sisa stok ${data.material.stok} (${data.material.jenis})`
      );
      setQty("");
      setCatatan("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencatat mutasi stok");
    } finally {
      setBusy(false);
    }
  };

  const movesView = sel ? moves.filter((m) => m.material_id === sel) : moves;

  return (
    <div className="space-y-5" data-testid="stock-board">
      <div className="grid gap-4 sm:grid-cols-4">
        <Card label="Jenis Bahan" value={rows.length} warna="text-slate-100" />
        <Card label="Total Stok Masuk" value={totalMasuk.toLocaleString("id-ID")} warna="text-emerald-300" />
        <Card label="Total Stok Keluar" value={totalKeluar.toLocaleString("id-ID")} warna="text-rose-300" />
        <Card label="Bahan Stok Habis" value={habis} warna={habis ? "text-rose-300" : "text-slate-100"} />
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="stock-recap">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300">
            Rekap Stok Masuk & Keluar
          </h4>
          <Button
            data-testid="stock-export-btn"
            onClick={() =>
              window.open(`${API}/stock-moves/export?auth=${localStorage.getItem("pas_token")}`, "_blank")
            }
            className="h-10 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 text-xs font-semibold"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Excel
          </Button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["Kategori", "Jenis", "Merk", "Ukuran", "Total Masuk", "Total Keluar", "Stok Saat Ini", "Mutasi Terakhir", "User Terakhir"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="stock-recap-list">
              {rekap.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                    Belum ada data bahan.
                  </td>
                </tr>
              )}
              {rekap.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2 text-slate-200 font-medium">{r.kategori}</td>
                  <td className="px-3 py-2">{r.jenis}</td>
                  <td className="px-3 py-2 text-slate-400">{r.merk || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{r.ukuran || "-"}</td>
                  <td className="px-3 py-2 text-emerald-300 font-semibold">{r.masuk}</td>
                  <td className="px-3 py-2 text-rose-300 font-semibold">{r.keluar}</td>
                  <td className={`px-3 py-2 font-semibold ${Number(r.stok) <= 0 ? "text-rose-300" : "text-teal-300"}`}>
                    {r.stok}
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {r.last_tanggal ? fmtDate(r.last_tanggal) : "-"}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{r.last_by || "-"}</td>
                </tr>
              ))}
              <tr className="border-t border-white/10 bg-slate-900/60 font-semibold">
                <td className="px-3 py-2" colSpan={4}>TOTAL</td>
                <td className="px-3 py-2 text-emerald-300">{totalMasuk}</td>
                <td className="px-3 py-2 text-rose-300">{totalKeluar}</td>
                <td className="px-3 py-2 text-teal-300">
                  {rekap.reduce((a, r) => a + Number(r.stok || 0), 0)}
                </td>
                <td className="px-3 py-2" colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="stock-empty-recap">
        <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-rose-300">
          Rekap Bahan Habis / Stok Kosong ({rows.filter((r) => Number(r.stok || 0) <= 0).length})
        </h4>
        <div className="overflow-x-auto rounded-xl border border-rose-500/20">
          <table className="w-full text-xs">
            <thead className="bg-rose-500/10 text-rose-200 uppercase text-[10px] tracking-wider">
              <tr>
                {["Kategori", "Jenis", "Merk", "Ukuran", "Stok", "Mutasi Terakhir", "User Terakhir"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="stock-empty-list">
              {rekap.filter((r) => Number(r.stok || 0) <= 0).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                    Tidak ada bahan yang habis. Semua stok tersedia.
                  </td>
                </tr>
              )}
              {rekap
                .filter((r) => Number(r.stok || 0) <= 0)
                .map((r) => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-slate-800/40">
                    <td className="px-3 py-2 text-slate-200 font-medium">{r.kategori}</td>
                    <td className="px-3 py-2">{r.jenis}</td>
                    <td className="px-3 py-2 text-slate-400">{r.merk || "-"}</td>
                    <td className="px-3 py-2 text-slate-400">{r.ukuran || "-"}</td>
                    <td className="px-3 py-2 font-semibold text-rose-300">{r.stok}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {r.last_tanggal ? fmtDate(r.last_tanggal) : "-"}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{r.last_by || "-"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-4" data-testid="stock-move-form">
        <div className="flex items-center gap-2">
          <Boxes className="w-5 h-5 text-teal-400" />
          <h3 className="font-display text-lg font-semibold">Entri Stok Masuk / Keluar</h3>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Bahan">
            <select
              data-testid="stock-material-select"
              value={sel}
              onChange={(e) => setSel(e.target.value)}
              className="h-11 min-w-[260px] rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
            >
              <option value="">— pilih bahan —</option>
              {rows.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.kategori} · {r.jenis} {r.merk ? `· ${r.merk}` : ""} {r.ukuran ? `· ${r.ukuran}` : ""} (stok {r.stok})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Jenis Mutasi">
            <select
              data-testid="stock-jenis-select"
              value={jenis}
              onChange={(e) => setJenis(e.target.value)}
              className="h-11 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
            >
              <option value="masuk">Stok Masuk</option>
              <option value="keluar">Stok Keluar</option>
            </select>
          </Field>
          <Field label="Jumlah">
            <Input
              data-testid="stock-qty-input"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="0"
              className="h-11 w-28 bg-slate-900/70 border-white/10"
            />
          </Field>
          <Field label="Tanggal">
            <Input
              data-testid="stock-date-input"
              type="date"
              value={tanggal}
              onChange={(e) => setTanggal(e.target.value)}
              className="h-11 bg-slate-900/70 border-white/10"
            />
          </Field>
          <Field label="Catatan">
            <Input
              data-testid="stock-note-input"
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              placeholder="mis. pemakaian JO 0012"
              className="h-11 w-56 bg-slate-900/70 border-white/10"
            />
          </Field>
          <Button
            data-testid="stock-submit-btn"
            onClick={simpan}
            disabled={busy || !sel || !qty}
            className={`h-11 rounded-xl px-6 font-semibold ${
              jenis === "masuk" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"
            }`}
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : jenis === "masuk" ? (
              <><ArrowDownCircle className="w-4 h-4 mr-2" /> Simpan Stok Masuk</>
            ) : (
              <><ArrowUpCircle className="w-4 h-4 mr-2" /> Simpan Stok Keluar</>
            )}
          </Button>
        </div>
      </div>

      {canDetail && (
      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300">
            Detail Stok Bahan
          </h4>
          <div className="flex flex-wrap items-center gap-3">
            <select
              data-testid="stock-kategori-filter"
              value={katFilter}
              onChange={(e) => setKatFilter(e.target.value)}
              className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="ALL">Semua Kategori</option>
              {kategoriOptions.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="stock-search-input"
                placeholder="cari jenis / merk / ukuran"
                value={cari}
                onChange={(e) => setCari(e.target.value)}
                className="h-10 pl-9 w-64 bg-slate-900/70 border-white/10"
              />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["Kategori", "Jenis", "Merk", "Ukuran", "Harga", "Stok", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="stock-list">
              {view.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Belum ada bahan. Tambahkan di menu Kelola Stok Bahan.
                  </td>
                </tr>
              )}
              {view.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t border-white/5 hover:bg-slate-800/40 ${
                    sel === r.id ? "bg-slate-800/50" : ""
                  }`}
                >
                  <td className="px-3 py-2 text-slate-200 font-medium">{r.kategori}</td>
                  <td className="px-3 py-2">{r.jenis}</td>
                  <td className="px-3 py-2 text-slate-400">{r.merk || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{r.ukuran || "-"}</td>
                  <td className="px-3 py-2">{rp(r.harga)}</td>
                  <td
                    className={`px-3 py-2 font-semibold ${
                      Number(r.stok) <= 0 ? "text-rose-300" : "text-teal-300"
                    }`}
                  >
                    {r.stok}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`stock-pick-${r.id}`}
                      onClick={() => setSel(r.id)}
                      className="rounded-lg border border-white/10 bg-slate-800/70 px-3 py-1 text-[10px] font-semibold uppercase hover:bg-slate-700"
                    >
                      Pilih
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300">
            Riwayat Mutasi Stok {sel ? "(bahan terpilih)" : "(semua bahan)"}
          </h4>          {sel && (
            <button
              type="button"
              data-testid="stock-clear-selection"
              onClick={() => setSel("")}
              className="rounded-lg border border-white/10 bg-slate-800/70 px-3 py-1 text-[10px] font-semibold uppercase hover:bg-slate-700"
            >
              Tampilkan Semua
            </button>
          )}
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10 max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider sticky top-0">
              <tr>
                {["Tanggal", "Bahan", "Mutasi", "Jumlah", "Stok Akhir", "Catatan", "Oleh"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="stock-moves-list">
              {movesView.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Belum ada mutasi stok.
                  </td>
                </tr>
              )}
              {movesView.map((m) => (
                <tr key={m.id} className="border-t border-white/5">
                  <td className="px-3 py-2 text-slate-400">{fmtDate(m.tanggal)}</td>
                  <td className="px-3 py-2">
                    {m.kategori} · {m.jenis_bahan} {m.merk ? `· ${m.merk}` : ""}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-lg px-2 py-1 text-[10px] font-semibold uppercase ${
                        m.jenis === "masuk"
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-rose-500/15 text-rose-300"
                      }`}
                    >
                      {m.jenis}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-semibold">{m.qty}</td>
                  <td className="px-3 py-2 text-slate-300">{m.stok_akhir}</td>
                  <td className="px-3 py-2 text-slate-400">{m.catatan || "-"}</td>
                  <td className="px-3 py-2 text-slate-400">{m.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
